import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { NewUserQuizStatus } from '@/shared/api/newUserQuiz';
import type { ReactNode } from 'react';

import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';

import { useBalanceStore } from '@/entities/balance';
import {
  isNewUserQuizCandidate,
  isNewUserQuizLaunchUser,
  useCurrentUser,
} from '@/entities/user/currentUser';

import { IS_WHITE_LABEL } from '@/shared/environments';

import { getActiveNewUserQuizBypassExpiresAt } from '../lib/bypass';
import { getNewUserQuizStatusQueryKey, useNewUserQuizStatus } from '../model/useNewUserQuizStatus';
import { NewUserQuiz } from './NewUserQuiz';

type NewUserQuizGateProps = {
  children: ReactNode;
};

export const NewUserQuizGate = ({ children }: NewUserQuizGateProps) => {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const { hasTrialPlan, isBalanceLoaded } = useBalanceStore();
  const [bypassCheckAt, setBypassCheckAt] = useState(Date.now);
  const [dismissedUserId, setDismissedUserId] = useState<null | string>(null);
  const [startedUserId, setStartedUserId] = useState<null | string>(null);
  const reportedStatusErrorUserIdRef = useRef<null | string>(null);
  const bypassExpiresAt = useMemo(
    () => (user?.id ? getActiveNewUserQuizBypassExpiresAt(user.id, bypassCheckAt) : null),
    [bypassCheckAt, user?.id],
  );

  const isCandidate = isNewUserQuizCandidate({
    hasTrialPlan,
    isBalanceLoaded,
    isWhiteLabel: IS_WHITE_LABEL,
    user,
  });
  const isPotentialCandidate =
    !IS_WHITE_LABEL && !user?.is_superuser && isNewUserQuizLaunchUser(user);
  const isDismissed = dismissedUserId === user?.id;
  const isBypassed = bypassExpiresAt !== null;
  const isFlowStarted = startedUserId === user?.id;
  const statusQuery = useNewUserQuizStatus(user?.id, isCandidate && !isBypassed && !isDismissed);
  const isConfirmedIncomplete =
    statusQuery.data?.completed === false && !statusQuery.isError && !statusQuery.isFetching;

  const setCompletedStatus = useCallback(() => {
    if (!user?.id) {
      return;
    }

    setStartedUserId(user.id);
    queryClient.setQueryData<NewUserQuizStatus>(getNewUserQuizStatusQueryKey(user.id), { completed: true });
  }, [queryClient, user?.id]);

  const bypass = useCallback(() => {
    setDismissedUserId(user?.id ?? null);
    setBypassCheckAt(Date.now());
  }, [user?.id]);

  const dismiss = useCallback(() => {
    if (!user?.id) {
      return;
    }

    setDismissedUserId(user.id);
    setBypassCheckAt(Date.now());
    queryClient.setQueryData<NewUserQuizStatus>(getNewUserQuizStatusQueryKey(user.id), { completed: true });
    void queryClient.invalidateQueries({ queryKey: getNewUserQuizStatusQueryKey(user.id) });
  }, [queryClient, user?.id]);

  useEffect(() => {
    setDismissedUserId(null);
    setStartedUserId(null);
    reportedStatusErrorUserIdRef.current = null;
  }, [user?.id]);

  useEffect(() => {
    if (bypassExpiresAt === null || !user?.id) {
      return undefined;
    }

    const bypassedUserId = user.id;
    const timer = window.setTimeout(() => {
      setBypassCheckAt(Date.now());
      setDismissedUserId((current) => (current === bypassedUserId ? null : current));
    }, Math.max(0, bypassExpiresAt - Date.now()));

    return () => window.clearTimeout(timer);
  }, [bypassExpiresAt, user?.id]);

  useEffect(() => {
    if (user?.id && isCandidate && isConfirmedIncomplete) {
      setStartedUserId(user.id);
    }
  }, [isCandidate, isConfirmedIncomplete, user?.id]);

  useEffect(() => {
    if (user?.id && isCandidate && statusQuery.isError && reportedStatusErrorUserIdRef.current !== user.id) {
      reportedStatusErrorUserIdRef.current = user.id;
      Sentry.captureException(statusQuery.error, { tags: { new_user_quiz_status: 'load_failed' } });
    }
  }, [isCandidate, statusQuery.error, statusQuery.isError, user?.id]);

  if (isPotentialCandidate && !isBalanceLoaded && !isBypassed) {
    return null;
  }

  if (
    isCandidate &&
    !isBypassed &&
    !isDismissed &&
    !isFlowStarted &&
    (statusQuery.isPending || (statusQuery.data?.completed === false && statusQuery.isFetching))
  ) {
    return null;
  }

  if (
    user &&
    isCandidate &&
    !isDismissed &&
    !isBypassed &&
    (isFlowStarted || isConfirmedIncomplete)
  ) {
    return (
      <NewUserQuiz
        key={user.id}
        email={user.email}
        userId={user.id}
        onBypass={bypass}
        onCompleted={setCompletedStatus}
        onDismiss={dismiss}
      />
    );
  }

  return children;
};
